import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { LABEL_CATEGORY, type LabelCategory } from '@vagusx/shared';
import { SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { SheetActions, SheetBody } from '@/components/custom/sheet-layout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { SaveButton } from '@/components/custom/save-button';
import { DeleteButton } from '@/components/custom/delete-button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { AutoGrowTextarea } from '@/components/custom/auto-grow-textarea';
import { cn } from '@/lib/utils';
import { useLabels, useCreateLabel, useUpdateLabel, useDeleteLabel } from '@/hooks/use-labels';
import { LabelBadge } from '@/components/custom/label-badge';
import { CATEGORY_OPTIONS, categoryLabel, coerceCategory } from './categories';

const COLOR_OPTIONS = [
  'blue',
  'red',
  'green',
  'purple',
  'amber',
  'orange',
  'cyan',
  'slate',
  'indigo',
  'gray',
] as const;

const colorPreviewMap: Record<string, string> = {
  blue: 'bg-blue-500',
  red: 'bg-red-500',
  green: 'bg-green-500',
  purple: 'bg-purple-500',
  amber: 'bg-amber-500',
  orange: 'bg-orange-500',
  cyan: 'bg-cyan-500',
  slate: 'bg-slate-500',
  indigo: 'bg-indigo-500',
  gray: 'bg-gray-500',
};

/**
 * Auto-generate a slug from a display name.
 * "My Custom Label" → "my_custom_label"
 */
function toSlug(displayName: string): string {
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 50);
}

interface FormValues {
  displayName: string;
  description: string;
  classificationPrompt: string;
  color: string;
  enabled: boolean;
  labelCategory: LabelCategory;
}

interface LabelEditorProps {
  labelId?: string;
  onClose: () => void;
}

export function LabelEditor({ labelId, onClose }: LabelEditorProps): React.ReactElement {
  const isEdit = !!labelId;
  const { data: labels } = useLabels();
  const createLabel = useCreateLabel();
  const updateLabel = useUpdateLabel();
  const deleteLabel = useDeleteLabel();

  const existingLabel = useMemo(() => labels?.find((l) => l.id === labelId), [labels, labelId]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      displayName: '',
      description: '',
      classificationPrompt: '',
      color: 'blue',
      enabled: true,
      labelCategory: LABEL_CATEGORY.WORKFLOW_TAG,
    },
  });

  // Load existing label data
  useEffect(() => {
    if (existingLabel) {
      reset({
        displayName: existingLabel.displayName,
        description: existingLabel.description ?? '',
        classificationPrompt: existingLabel.classificationPrompt ?? '',
        color: existingLabel.color ?? 'blue',
        enabled: existingLabel.enabled,
        labelCategory: coerceCategory(existingLabel.labelCategory),
      });
    }
  }, [existingLabel, reset]);

  const selectedColor = watch('color');
  const currentEnabled = watch('enabled');
  const currentDisplayName = watch('displayName');
  const currentCategory = watch('labelCategory');

  const onSubmit = async (data: FormValues): Promise<void> => {
    if (isEdit && labelId) {
      if (existingLabel?.isSystem) {
        // System labels: only update color and enabled
        await updateLabel.mutateAsync({
          id: labelId,
          data: {
            color: data.color,
            enabled: data.enabled,
          },
        });
      } else {
        await updateLabel.mutateAsync({
          id: labelId,
          data: {
            displayName: data.displayName,
            description: data.description || undefined,
            classificationPrompt: data.classificationPrompt || null,
            color: data.color,
            enabled: data.enabled,
            labelCategory: data.labelCategory,
          },
        });
      }
    } else {
      // Auto-generate slug from display name on create
      await createLabel.mutateAsync({
        name: toSlug(data.displayName),
        displayName: data.displayName,
        description: data.description || undefined,
        classificationPrompt: data.classificationPrompt || undefined,
        color: data.color,
        labelCategory: data.labelCategory,
      });
    }
    onClose();
  };

  const handleDelete = async (): Promise<void> => {
    if (labelId) {
      await deleteLabel.mutateAsync(labelId);
      onClose();
    }
  };

  const isSystem = existingLabel?.isSystem ?? false;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
      <SheetHeader>
        <SheetTitle>
          {isEdit ? (
            <span className="flex items-center gap-2">
              {existingLabel?.displayName ?? 'Edit Label'}
              {isSystem && (
                <Badge variant="outline" className="text-xs">
                  System
                </Badge>
              )}
            </span>
          ) : (
            'Add Label'
          )}
        </SheetTitle>
        <SheetDescription>
          {isEdit
            ? isSystem
              ? 'System labels can be enabled/disabled and their color changed.'
              : 'Edit this custom classification label.'
            : 'Create a new custom classification label.'}
        </SheetDescription>
      </SheetHeader>

      <SheetBody>
        <div className="space-y-4">
          {/* Preview */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Preview:</span>
            <LabelBadge
              label={{ displayName: currentDisplayName || 'Label', color: selectedColor }}
            />
          </div>

          <Separator />

          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="displayName">Name</Label>
            {isSystem ? (
              <p className="text-sm">{existingLabel?.displayName}</p>
            ) : (
              <>
                <Input
                  id="displayName"
                  placeholder="e.g. Prior Authorization"
                  {...register('displayName', { required: 'Name is required' })}
                />
                {errors.displayName && (
                  <p className="text-xs text-destructive">{errors.displayName.message}</p>
                )}
              </>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            {isSystem ? (
              <p className="text-sm text-muted-foreground">
                {existingLabel?.description ?? 'No description'}
              </p>
            ) : (
              <AutoGrowTextarea
                id="description"
                minRows={2}
                maxRows={5}
                placeholder="Short description shown in the UI..."
                {...register('description')}
              />
            )}
          </div>

          {/* Classification Prompt */}
          <div className="space-y-2">
            <Label htmlFor="classificationPrompt">Classification Prompt</Label>
            {isSystem ? (
              <p className="text-sm text-muted-foreground">
                {existingLabel?.classificationPrompt ?? 'No prompt defined'}
              </p>
            ) : (
              <>
                <AutoGrowTextarea
                  id="classificationPrompt"
                  minRows={3}
                  maxRows={10}
                  placeholder="Describe what documents should be classified under this label. Be specific about content patterns, keywords, and document types the AI should look for..."
                  {...register('classificationPrompt')}
                />
                <p className="text-xs text-muted-foreground">
                  Detailed instructions for the AI classifier. If empty, the description is used
                  instead.
                </p>
              </>
            )}
          </div>

          <Separator />

          {/* Category */}
          {isSystem ? (
            <div className="space-y-2">
              <Label>Category</Label>
              <p className="text-sm">
                {existingLabel ? categoryLabel(existingLabel.labelCategory) : ''}
              </p>
            </div>
          ) : (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium leading-none">Category</legend>
              <div className="space-y-1.5">
                {CATEGORY_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors',
                      currentCategory === opt.value
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-muted/40'
                    )}
                  >
                    <input
                      type="radio"
                      name="labelCategory"
                      value={opt.value}
                      checked={currentCategory === opt.value}
                      onChange={() => setValue('labelCategory', opt.value, { shouldDirty: true })}
                      className="mt-1"
                    />
                    <div className="flex min-w-0 flex-col">
                      <span className="text-sm font-medium">{opt.label}</span>
                      <span className="text-xs text-muted-foreground">{opt.help}</span>
                    </div>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          <Separator />

          {/* Color */}
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setValue('color', color)}
                  className={cn(
                    'h-7 w-7 rounded-full border-2 transition-all',
                    colorPreviewMap[color],
                    selectedColor === color
                      ? 'border-foreground scale-110'
                      : 'border-transparent opacity-60 hover:opacity-100'
                  )}
                  title={color}
                />
              ))}
            </div>
          </div>

          {/* Enabled toggle */}
          {isEdit && (
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="enabled">Enabled</Label>
                <p className="text-xs text-muted-foreground">
                  Disabled labels won't appear in classification results
                </p>
              </div>
              <Switch
                id="enabled"
                checked={currentEnabled}
                onCheckedChange={(checked: boolean) => setValue('enabled', checked)}
              />
            </div>
          )}
        </div>
      </SheetBody>

      <SheetActions
        start={
          isEdit &&
          !isSystem && <DeleteButton isPending={deleteLabel.isPending} onClick={handleDelete} />
        }
      >
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        {isEdit ? (
          <SaveButton type="submit" isPending={updateLabel.isPending} isEdit />
        ) : (
          <Button type="submit" disabled={createLabel.isPending}>
            Create Label
          </Button>
        )}
      </SheetActions>
    </form>
  );
}
